// Always use relative API paths - nginx proxy handles routing to backend
const API_BASE_URL = '/api';

export interface SchemaInfo {
  current: string;
  supported: string[];
  deprecated: string[];
  table: string;
  field: string;
}

export interface SchemaVersionResponse {
  schemaType: string;
  currentVersion: string;
}

export interface SchemaInfoResponse {
  schemaType: string;
  current: string;
  supported: string[];
  deprecated: string[];
  table: string;
  field: string;
}

class SchemaService {
  private versionCache: Map<string, string> = new Map();

  /**
   * Get the current schema version for a schema type
   */
  async getCurrentVersion(schemaType: string): Promise<string> {
    // Check cache first
    if (this.versionCache.has(schemaType)) {
      return this.versionCache.get(schemaType)!;
    }

    try {
      // Use fetch directly since we need a simple GET request
      const response = await fetch(`${API_BASE_URL}/schemas/current/${schemaType}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json() as { data: SchemaVersionResponse };
      const version = result.data.currentVersion;
      this.versionCache.set(schemaType, version);
      return version;
    } catch (error) {
      console.error(`Failed to get current version for ${schemaType}:`, error);
      throw error;
    }
  }

  /**
   * Get the current schema version for products.productConfig
   */
  async getCurrentProductConfigVersion(): Promise<string> {
    return this.getCurrentVersion('products.productConfig');
  }

  /**
   * Get full schema information for a schema type
   */
  async getSchemaInfo(schemaType: string): Promise<SchemaInfo> {
    try {
      const response = await fetch(`${API_BASE_URL}/schemas/info/${schemaType}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json() as { data: SchemaInfoResponse };
      return {
        current: result.data.current,
        supported: result.data.supported,
        deprecated: result.data.deprecated,
        table: result.data.table,
        field: result.data.field,
      };
    } catch (error) {
      console.error(`Failed to get schema info for ${schemaType}:`, error);
      throw error;
    }
  }

  /**
   * Check if a schema version is supported
   */
  async isVersionSupported(schemaType: string, version: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/schemas/check/${schemaType}/${version}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json() as { data: { isSupported: boolean } };
      return result.data.isSupported;
    } catch (error) {
      console.error(`Failed to check version support for ${schemaType}@${version}:`, error);
      return false;
    }
  }

  /**
   * Clear the version cache
   */
  clearCache(): void {
    this.versionCache.clear();
  }
}

export const schemaService = new SchemaService();
